import React, { Component } from 'react';
import { Query, Mutation } from 'react-apollo';
import PropTypes from 'prop-types';

import {
  GET_DOCTOR_TIPS,
  CREATE_DOCTOR_TIP,
  DELETE_DOCTOR_TIP,
  UPDATE_DOCTOR_TIP,
  CREATE_DOCTOR_TIP_SUBSECTION,
  DELETE_DOCTOR_TIP_SUBSECTION,
  UPDATE_DOCTOR_TIP_SUBSECTION,
  GET_CONTENT_PAGE,
  UPDATE_CONTENT_PAGE,
  CREATE_CONTENT_PAGE
} from '../gql';

import Accordion from '../components/Accordion';
import Throbber from '../components/Throbber';
import ContentPanel from '../components/ContentPanel';
import WysiwygEditor from '../components/WysiwygEditor';
import VisibilityToggle from '../components/VisibilityToggle';
import ErrorBoundary from '../components/ErrorBoundary';

import bridgeIcon from './../../images/icons/bridge.svg';
import roundPlus from './../../images/icons/round-plus-white.svg';

class DoctorTips extends Component {
  constructor() {
    super();
    this.state = {
      tipUpdate: {},
      contenPageUpdate: {},
      publishIsUpToDate: null
    }
    this.submitDelay = 500;
  }

  componentDidMount() {
    const { client } = this.props;
    client.writeData({
      data: {
        currentContentType: 'DOCTOR_TIP'
      }
    });
  }

  componentWillUnmount() {
    const { client } = this.props;
    client.writeData({
      data: {
        unpublishWarning: false,
      }
    });
  }

  activateUnpublishedAlert() {
    const { client } = this.props;
    client.writeData({
      data: {
        unpublishWarning: true,
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { published } = this.props;

    if (!published && prevProps.published) {
      this.setState({
        publishIsUpToDate: false
      });
    }

    if (published && !prevProps.published) {
      this.setState({
        publishIsUpToDate: true
      });
    }
  }

  initialContentData = data => {
    if (data === null) {
      return {
        document: {
          nodes: [
            {
              object: 'block',
              type: 'paragraph',
            }
          ]
        }
      }
    }
    return data;
  }

  activatePublishButton() {
    const { client } = this.props;
    client.writeData({
      data: {
        publishDisabled: false,
        queryRefetchAfterPublish: ['GET_DOCTOR_TIPS', 'GET_CONTENT_PAGE'],
        queryRefetchContentType: 'DOCTOR_TIP',
      }
    });
  }

  createContentPage(createContentPage) {
    const {
      languageCode,
      published,
      app
    } = this.props;

    createContentPage({
      variables: {
        title: '',
        type: 'DOCTOR_TIP',
        languageCode,
        app,
        published
      }
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  submitContentUpdate(updateContentPage) {
    const { contenPageUpdate } = this.state;
    updateContentPage({
      variables: contenPageUpdate
    });
    this.setState({
      contenPageUpdate: {}
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  submitDoctorTipUpdate(updateFunc) {
    const { tipUpdate } = this.state;
    updateFunc({
      variables: tipUpdate
    });
    this.setState({
      tipUpdate: {}
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  onPageInputChanges(event, id, updateContentPage) {
    const {
      languageCode,
      app
    } = this.props;
    const name = event.target.name;
    const value = event.target.value;

    clearTimeout(this.contentTimer);
    this.contentTimer = setTimeout(() => this.submitContentUpdate(updateContentPage), this.submitDelay);

    this.setState(prevState => {
      if (prevState.contenPageUpdate && prevState.contenPageUpdate.id === id) {
        return {
          contenPageUpdate: {
            ...this.state.contenPageUpdate,
            [name]: value
          }
        }
      }
      return {
        contenPageUpdate: {
          [name]: value,
          type: 'DOCTOR_TIP',
          languageCode,
          published: false,
          app,
          id
        }
      }
    });
  }

  onEditorChange(value, id, updateDoctorTip) {
    const {
      app,
      languageCode
    } = this.props;

    clearTimeout(this.editorTimer);
    this.editorTimer = setTimeout(() => {
      updateDoctorTip({
        variables: {
          id,
          app,
          published: false,
          languageCode,
          content: value
        }
      });
      this.activatePublishButton();
      this.activateUnpublishedAlert();
    }, this.submitDelay);
  }

  onInputChanges(event, id, updateFunc) {
    const {
      app,
      languageCode
    } = this.props;
    const name = event.target.name;
    const value = isNaN(event.target.value)
      ? event.target.value
      : Number(event.target.value);

    clearTimeout(this.inputTimer);
    this.inputTimer = setTimeout(() => this.submitDoctorTipUpdate(updateFunc), this.submitDelay);

    this.setState(prevState => {
      if (prevState.tipUpdate && prevState.tipUpdate.id === id) {
        return {
          tipUpdate: {
            ...this.state.tipUpdate,
            id,
            [name]: value
          }
        }
      }
      return {
        tipUpdate: {
          id,
          app,
          published: false,
          languageCode,
          [name]: value
        }
      }
    });
  }

  createDoctorTip(createDoctorTip, doctorTips) {
    const {
      languageCode,
      app
    } = this.props;

    const orderIndex = doctorTips.length > 0
      ? doctorTips[doctorTips.length - 1].meta.displayOrder + 1
      : 0;

    createDoctorTip({
      variables: {
        title: '',
        languageCode: languageCode,
        app: app,
        displayOrder: orderIndex
      }
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  deleteDoctorTip(id, deleteDoctorTip) {
    if (confirm('Are you sure you want to delete this doctor tip?')) {
      deleteDoctorTip({
        variables: {
          id
        }
      });
      this.activatePublishButton();
      this.activateUnpublishedAlert();
    }
  }

  createDoctorTipSubsection(createDoctorTipSubsection, subsections, doctorTipId) {
    const {
      languageCode,
      app
    } = this.props;

    const orderIndex = subsections.length > 0
      ? subsections[subsections.length - 1].meta.displayOrder + 1
      : 0;

    createDoctorTipSubsection({
      variables: {
        languageCode: languageCode,
        app: app,
        content: {
          document: {
            nodes: [
              {
                object: 'block',
                type: 'paragraph',
              },
            ],
          },
        },
        title: '',
        doctorTipId: doctorTipId,
        displayOrder: orderIndex,
        hiddenFromDashboard: false,
        shownRandomly: true
      }
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  deleteDoctorTipSubsection(deleteDoctorTipSubsection, id) {
    if (confirm('Are you sure you want to delete this subsection?')) {
      deleteDoctorTipSubsection({
        variables: {
          id
        }
      });
      this.activatePublishButton();
      this.activateUnpublishedAlert();
    }
  }

  onChangeEditor(value, id, updateDoctorTipSubsection) {
    const {
      app,
      languageCode
    } = this.props;

    clearTimeout(this.editorTimer);
    this.editorTimer = setTimeout(() => {
      updateDoctorTipSubsection({
        variables: {
          id,
          app,
          languageCode,
          published: false,
          content: value
        }
      });
      this.activatePublishButton();
      this.activateUnpublishedAlert();
    }, this.submitDelay);
  }

  onVisibilityChange(event, id, updateDoctorTipSubsection) {
    const {
      app,
      languageCode
    } = this.props;
    updateDoctorTipSubsection({
      variables: {
        id,
        app,
        languageCode,
        published: false,
        hiddenFromDashboard: event === 'hiddenFromDashboard'
          ? true
          : false,
        shownRandomly: event === 'hiddenFromDashboard'
          ? false
          : true
      }
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  updateDisplayOrder(id, order, updateFunc) {
    const {
      app,
      languageCode
    } = this.props;

    updateFunc({
      variables: {
        id,
        app,
        languageCode,
        published: false,
        displayOrder: order
      }
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  onIsBornChange(id, value, updateFunc) {
    const {
      app,
      languageCode,
    } = this.props;
    const isBorn = value ? false : true;

    updateFunc({
      variables: {
        app,
        languageCode,
        id,
        isBorn,
        published: false,
      }
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  publishNotification() {
    const { publishIsUpToDate } = this.state;

    if (publishIsUpToDate === null) {
      this.activatePublishButton();
    } else if (publishIsUpToDate) {
      return;
    }

    return (
      <div className="notification notification--warning" key="notification">
        <p className="notification__description">
          The content below has not been published yet.
        </p>
      </div>
    );
  }

  render() {
    const {
      app,
      languageCode,
      published
    } = this.props;

    return (
      <Query
        query={GET_DOCTOR_TIPS}
        variables={{
          languageCode: languageCode,
          app: app,
          limit: 1000
        }}>
        {({ data, loading, error }) => {
          if (loading)
            return <p className="full-width center-align">Loading...</p>;
          if (error)
            return <p className="full-width center-align">{error.message}</p>;
          const { doctorTips } = data;

          return (
            <Query
              query={GET_CONTENT_PAGE}
              variables={{
                type: 'DOCTOR_TIP',
                languageCode,
                app
              }}>
              {({ data, loading, error }) => {
                if (loading)
                  return <p className="full-width center-align">Loading...</p>;
                if (error)
                  return <p className="full-width center-align">{error.message}</p>;
                const { contentPage } = data;

                return (
                  contentPage !== null ? (
                    <Mutation
                      mutation={UPDATE_CONTENT_PAGE}
                      refetchQueries={[{
                        query: GET_CONTENT_PAGE,
                        variables: {
                          type: 'DOCTOR_TIP',
                          languageCode,
                          app
                        }
                      }]}
                    >
                      {updateContentPage => [
                        !contentPage.availability.published &&
                          this.publishNotification(),
                        <Accordion
                          key="accordion"
                          id={contentPage.id}
                          title={contentPage.title}
                          subtitle={contentPage.subtitle}
                          twoHeaderInputs
                          headerIcon={bridgeIcon}
                          onChanges={event => this.onPageInputChanges(event, contentPage.id, updateContentPage)}>
                          {doctorTips.edges.length > 0 ? (
                            doctorTips.edges
                              .sort((a, b) => a.meta.displayOrder - b.meta.displayOrder)
                              .map(doctorTip =>
                                <Mutation
                                  key={doctorTip.id}
                                  mutation={DELETE_DOCTOR_TIP}
                                  refetchQueries={[{
                                    query: GET_DOCTOR_TIPS,
                                    variables: {
                                      languageCode: languageCode,
                                      app: app,
                                      limit: 1000
                                    }
                                  }]}
                                >
                                  {(deleteDoctorTip, { loading, error }) => (
                                    <div className="relative-block">
                                      {error &&
                                        <p className="full-width center-align">{error.message}</p>
                                      }
                                      <Mutation
                                        mutation={UPDATE_DOCTOR_TIP}
                                        refetchQueries={[{
                                          query: GET_DOCTOR_TIPS,
                                          variables: {
                                            languageCode: languageCode,
                                            app: app,
                                            limit: 1000
                                          }
                                        }]}
                                      >
                                        {updateDoctorTip => [
                                          !doctorTip.availability.published &&
                                            this.publishNotification(),
                                          <Accordion
                                            key="accordion"
                                            id={doctorTip.id}
                                            title={doctorTip.title}
                                            subtitle={doctorTip.subtitle}
                                            twoHeaderInputs
                                            enableOrdering
                                            enableWeekRange
                                            isBorn={doctorTip.availability.isBorn}
                                            rangeFrom={doctorTip.availability.fromWeek}
                                            rangeTo={doctorTip.availability.toWeek}
                                            displayOrder={doctorTip.meta.displayOrder}
                                            siblingAccordions={doctorTips.edges}
                                            onIsBornChange={(id, value) => this.onIsBornChange(id, value, updateDoctorTip)}
                                            updateDisplayOrder={(id, order) => this.updateDisplayOrder(id, order, updateDoctorTip)}
                                            deleteAccordion={() => this.deleteDoctorTip(doctorTip.id, deleteDoctorTip)}
                                            onChanges={event => this.onInputChanges(event, doctorTip.id, updateDoctorTip)}>
                                            {doctorTip.subsections
                                              .sort((a, b) => a.meta.displayOrder - b.meta.displayOrder)
                                              .map(subsection =>
                                                <Mutation
                                                  key={subsection.id}
                                                  mutation={DELETE_DOCTOR_TIP_SUBSECTION}
                                                  refetchQueries={[{
                                                    query: GET_DOCTOR_TIPS,
                                                    variables: {
                                                      languageCode: languageCode,
                                                      app: app,
                                                      limit: 1000
                                                    }
                                                  }]}
                                                >
                                                  {(deleteDoctorTipSubsection, { loading, error })  => (
                                                    <div className="relative-block">
                                                      {error &&
                                                        <p className="full-width center-align">{error.message}</p>
                                                      }
                                                      <Mutation
                                                        mutation={UPDATE_DOCTOR_TIP_SUBSECTION}
                                                        refetchQueries={[{
                                                          query: GET_DOCTOR_TIPS,
                                                          variables: {
                                                            languageCode: languageCode,
                                                            app: app,
                                                            limit: 1000
                                                          }
                                                        }]}
                                                      >
                                                        {updateDoctorTipSubsection => [
                                                          !subsection.availability.published &&
                                                            this.publishNotification(),
                                                          <ContentPanel
                                                            key="subsection"
                                                            enableWeekRange
                                                            enableOrdering
                                                            id={subsection.id}
                                                            title={subsection.title}
                                                            rangeFrom={subsection.availability.fromWeek}
                                                            rangeTo={subsection.availability.toWeek}
                                                            isBorn={subsection.availability.isBorn}
                                                            displayOrder={subsection.meta.displayOrder}
                                                            siblingPanels={doctorTip.subsections}
                                                            onIsBornChange={(id, value) => this.onIsBornChange(id, value, updateDoctorTipSubsection)}
                                                            updateDisplayOrder={(id, order) => this.updateDisplayOrder(id, order, updateDoctorTipSubsection)}
                                                            deletePanel={() => this.deleteDoctorTipSubsection(deleteDoctorTipSubsection, subsection.id)}
                                                            onChanges={event => this.onInputChanges(event, subsection.id, updateDoctorTipSubsection)}>
                                                            <ErrorBoundary>
                                                              <WysiwygEditor
                                                                enableArticleLinking
                                                                enableExternalLinking
                                                                enableImageUpload
                                                                enableVimeoEmbed
                                                                enableEmbeds
                                                                enableAudioUpload
                                                                languageCode={languageCode}
                                                                app={app}
                                                                published={published}
                                                                editorData={this.initialContentData(subsection.content)}
                                                                onChangeValue={event => this.onEditorChange(event, subsection.id, updateDoctorTipSubsection)}
                                                              />
                                                            </ErrorBoundary>
                                                            <div className="content-panel__footer">
                                                              <div className="content-panel__actions">
                                                                <VisibilityToggle
                                                                  parentId={subsection.id}
                                                                  onChange={event => this.onVisibilityChange(event, subsection.id, updateDoctorTipSubsection)}
                                                                  options={[
                                                                    {id: 'shownRandomly', checked: subsection.availability.shownRandomly},
                                                                    {id: 'hiddenFromDashboard', checked: subsection.availability.hiddenFromDashboard}
                                                                  ]}
                                                                />
                                                              </div>
                                                            </div>
                                                          </ContentPanel>
                                                        ]}
                                                      </Mutation>
                                                      <Throbber
                                                        hidden={!loading}
                                                        loadingText="Deleting subsection..."
                                                      />
                                                    </div>
                                                  )}
                                                </Mutation>
                                              )}
                                            <Mutation
                                              mutation={CREATE_DOCTOR_TIP_SUBSECTION}
                                              refetchQueries={[{
                                                query: GET_DOCTOR_TIPS,
                                                variables: {
                                                  languageCode: languageCode,
                                                  app: app,
                                                  limit: 1000
                                                }
                                              }]}>
                                              {(createDoctorTipSubsection, { loading, error }) => (
                                                <div className="full-width right-align relative-block u-margin--top-medium">
                                                  <Throbber
                                                    hidden={!loading}
                                                    loadingText="Creating subsection..."
                                                  />
                                                  {error &&
                                                    <p className="full-width center-align">{error.message}</p>
                                                  }
                                                  <button
                                                    className="button button--small button--icon-text"
                                                    disabled={loading}
                                                    onClick={() => this.createDoctorTipSubsection(createDoctorTipSubsection, doctorTip.subsections, doctorTip.id)}>
                                                    <img src={roundPlus} alt="Add new subsection" className="button__icon" />
                                                    Add new subsection
                                                  </button>
                                                </div>
                                              )}
                                            </Mutation>
                                          </Accordion>
                                        ]}
                                      </Mutation>
                                      <Throbber
                                        hidden={!loading}
                                        loadingText="Deleting tip..."
                                      />
                                    </div>
                                  )}
                                </Mutation>
                              )
                          ) : (
                            <p className="full-width center-align">No doctor tips found.</p>
                          )}
                          <Mutation
                            mutation={CREATE_DOCTOR_TIP}
                            refetchQueries={[{
                              query: GET_DOCTOR_TIPS,
                              variables: {
                                languageCode: languageCode,
                                app: app,
                                limit: 1000
                              }
                            }]}>
                            {(createDoctorTip, { loading, error }) => (
                              <div className="full-width right-align relative-block u-margin--top-medium">
                                <Throbber
                                  hidden={!loading}
                                  loadingText="Creating tip..."
                                />
                                {error &&
                                  <p className="full-width center-align">{error.message}</p>
                                }
                                <button
                                  className="button button--small button--icon-text"
                                  disabled={loading}
                                  onClick={() => this.createDoctorTip(createDoctorTip, doctorTips.edges)}>
                                  <img src={roundPlus} alt="Add new tip" className="button__icon" />
                                  Add new tip
                                </button>
                              </div>
                            )}
                          </Mutation>
                        </Accordion>
                      ]}
                    </Mutation>
                  ) : (
                    <div>
                      <p className="full-width center-align">
                        No content found for this page
                      </p>
                      <Mutation
                        mutation={CREATE_CONTENT_PAGE}
                        refetchQueries={[{
                          query: GET_CONTENT_PAGE,
                          variables: {
                            type: 'DOCTOR_TIP',
                            languageCode,
                            app,
                            limit: 1000
                          }
                        }]}>
                        {(createContentPage, { loading, error }) =>
                          <div className="relative-block">
                            {error &&
                              <p className="full-width center-align">{error.message}</p>
                            }
                            <div className="right-align">
                              <button
                                className="button button--small button--icon-text"
                                disabled={loading}
                                onClick={() => this.createContentPage(createContentPage)}>
                                <img src={roundPlus} alt="Add" className="button__icon" />
                                Add new content page
                              </button>
                            </div>
                            <Throbber
                              hidden={!loading}
                              loadingText="Creating content page..."
                            />
                          </div>
                        }
                      </Mutation>
                    </div>
                  )
                )  
              }}
            </Query>
          )
        }}
      </Query>
    );
  }
}

DoctorTips.propTypes = {
  languageCode: PropTypes.string.isRequired,
  published: PropTypes.bool.isRequired,
  app: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired
};

export default DoctorTips;